import {
  LIVE_PRODUCTION_URLS,
  createSessionRefresher,
  jwtNotExpired,
  sameSiteApiUrl,
} from '@self-restaurant/shared'
/**
 * Cliente HTTP para a API do self-restaurant.
 * Toda comunicação com o backend passa por aqui.
 */
import {
  getToken,
  getTenantSlug,
  clearToken,
  setToken,
  clearStoredTenantSlug,
  tenantSlugIsFromStorage,
} from '../auth.js'

/**
 * #319: o X-Tenant-Slug veio do localStorage e a API respondeu que esse
 * restaurante não existe/está apagado → o slug guardado é obsoleto.
 */
const STALE_TENANT_CODES = new Set(['TENANT_NOT_FOUND', 'TENANT_INACTIVE'])
export function isStaleTenantError(status: number, code: string | undefined): boolean {
  return (status === 404 || status === 403) && !!code && STALE_TENANT_CODES.has(code)
}
async function errorCode(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.clone().json()) as { error?: { code?: string } }
    return body.error?.code
  } catch {
    return undefined
  }
}

// Em dev local, a API roda em localhost:8787 (proxied pelo Vite).
// Em produção, é o mesmo hostname (/{tenant-slug}.self-restaurant.app).
// Em produção o admin fala DIRETO com a API: o proxy do admin.asknamba.com
// só repassa alguns caminhos (/auth, /admin) — /orders e /realtime caíam no
// catch-all do SPA (HTML em vez de JSON). Em dev, o proxy do Vite cobre tudo.
// CORS na API já aceita origens admin; wss não tem CORS.
// Produção: api.pratzu.com (fase 2). Staging (#118): build com
// VITE_API_URL=https://api-staging.pratzu.com. Painel aberto em
// admin(-staging).asknamba.com (em paralelo até a fase 3) fala com a API do
// mesmo site (api(-staging).asknamba.com) — refresh cookie SameSite=Strict.
export const BASE = import.meta.env.DEV
  ? ''
  : sameSiteApiUrl(
      (import.meta.env.VITE_API_URL as string | undefined) ?? LIVE_PRODUCTION_URLS.api,
      typeof window !== 'undefined' ? window.location.hostname : undefined,
    )

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  isFormData = false,
  retried = false,
  tenantSlug?: string,
): Promise<T> {
  const token = getToken()
  const slugFromStorage = !tenantSlug && tenantSlugIsFromStorage()
  const slug = tenantSlug ?? getTenantSlug()

  const headers: Record<string, string> = {
    'X-Tenant-Slug': slug,
  }
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (body && !isFormData) headers['Content-Type'] = 'application/json'

  const bodyContent: BodyInit | null = isFormData
    ? (body as FormData)
    : body !== undefined
      ? JSON.stringify(body)
      : null

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: bodyContent,
  })

  if (res.status === 401) {
    // Só tenta refresh se houver uma sessão ativa — sem token = fluxo de login (ex: OTP inválido)
    if (getToken() && !retried) {
      // Outra requisição/aba já renovou a sessão enquanto esta voava: só repetir.
      if (token && getToken() !== token && jwtNotExpired(getToken()!)) {
        return request<T>(method, path, body, isFormData, true, tenantSlug)
      }
      const refreshed = await refreshSession()
      if (refreshed) {
        return request<T>(method, path, body, isFormData, true, tenantSlug)
      }
      // refreshSession só limpa o token se ele ainda era o nosso; se outra aba
      // trocou o token, não derrubar a sessão dela.
      if (!getToken()) {
        window.location.href = '/login'
        throw new Error('Sessão expirada')
      }
    }
  }

  // 204 sem corpo (DELETE idempotente, ex.: /admin/push/subscriptions — #124):
  // res.json() estouraria em corpo vazio
  if (res.status === 204) return undefined as T

  const data = (await res.json()) as {
    ok: boolean
    data?: T
    error?: { code: string; message: string; details?: unknown }
  }

  if (!data.ok || !res.ok) {
    // Slug guardado de um tenant apagado: descartá-lo; o próximo pedido desta
    // página sai com o contexto da plataforma (#319)
    if (slugFromStorage && isStaleTenantError(res.status, data.error?.code)) clearStoredTenantSlug()
    // #430: sessão da plataforma terminou — limpa estado e vai à consola
    // (nunca um 403 genérico). Só reage se havia sessão local ativa.
    if (data.error?.code === 'IMPERSONATION_REQUIRED') {
      void import('../impersonation.js').then(
        ({ getImpersonationSession, markImpersonationExpired }) => {
          if (getImpersonationSession()) {
            markImpersonationExpired()
            if (!window.location.pathname.startsWith('/login')) {
              window.location.assign('/')
            }
          }
        },
      )
    }
    throw new ApiError(
      data.error?.message ?? `HTTP ${res.status}`,
      data.error?.code ?? 'UNKNOWN',
      res.status,
      data.error?.details,
    )
  }

  return data.data as T
}

// Renovação de sessão (hotfix #288): single-flight na aba + Web Lock entre
// abas. O refresh token é rotacionado a cada uso; refreshes paralelos (várias
// requisições 401 ou várias abas) faziam o perdedor receber 401, apagar o
// token compartilhado e mandar para /login: loop de autenticação.
export const refreshSession = createSessionRefresher({
  getToken,
  setToken,
  clearToken,
  isValid: jwtNotExpired,
  locks: typeof navigator !== 'undefined' && navigator.locks ? navigator.locks : undefined,
  refresh: async () => {
    const post = (slug: string) =>
      fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'X-Tenant-Slug': slug },
        credentials: 'include',
      })
    const fromStorage = tenantSlugIsFromStorage()
    let res = await post(getTenantSlug())
    // #319: o refresh não depende do tenant do header (vem do cookie), mas o
    // resolveTenant recusa um slug de tenant apagado/inativo — antes isso
    // derrubava a sessão. Um slug guardado obsoleto é descartado; o da URL
    // (ex.: /r/<inexistente>) fica. Repete com o contexto da plataforma.
    if (isStaleTenantError(res.status, await errorCode(res))) {
      if (fromStorage) clearStoredTenantSlug()
      res = await post('platform')
    }
    // 401/403 = sessão acabou; 5xx/429 = transitório (não derruba a sessão)
    if (res.status === 401 || res.status === 403) return null
    if (!res.ok) throw new Error(`refresh HTTP ${res.status}`)
    const data = (await res.json()) as { ok: boolean; data?: { access_token: string } }
    return data.ok && data.data ? data.data.access_token : null
  },
})

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    /** `error.details` da resposta (ex.: INCOMPLETE_HOURS → { days }) */
    public details?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const get = <T>(path: string) => request<T>('GET', path)
export const post = <T>(path: string, body: unknown) => request<T>('POST', path, body)
export const put = <T>(path: string, body: unknown) => request<T>('PUT', path, body)
export const patch = <T>(path: string, body: unknown) => request<T>('PATCH', path, body)
export const del = <T>(path: string) => request<T>('DELETE', path)
export const postForm = <T>(path: string, form: FormData) => request<T>('POST', path, form, true)
